import React from 'react';
import type { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import type { GetCommunitiesQuery_communities } from '@frontend/app/queries/types/GetCommunitiesQuery';
import { InstagramCreator } from '@frontend/gql/social/graphql';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@frontend/shadcn/components/ui/alert-dialog';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { Input } from '@frontend/shadcn/components/ui/input';
import { ChevronDownIcon } from '@revfluence/fresh-icons/solid/esm';

type ProgramItem = GetAllPrograms_programs;
type GroupItem = GetCommunitiesQuery_communities;

interface CreatorOverviewActionMenuProps {
  creator: InstagramCreator;
  filteredCampaigns: ProgramItem[];
  filteredGroups: GroupItem[];
  campaignSearchTerm: string;
  setCampaignSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  groupSearchTerm: string;
  setGroupSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  onInviteToCampaign: (program: ProgramItem) => void;
  onAddToGroup: (group: GroupItem) => void;
  onAddToCampaign: (program: ProgramItem) => void;
  igdmActive: boolean;
  disabled?: boolean;

  // Confirmation dialog props
  isInviteToCampaignConfirmOpen: boolean;
  pendingInviteToCampaign: ProgramItem | null;
  proceedWithInviteToCampaign: () => void;
  closeInviteToCampaignConfirmDialog: () => void;

  isAddToGroupConfirmOpen: boolean;
  pendingAddToGroup: GroupItem | null;
  proceedWithAddToGroup: () => void;
  closeAddToGroupConfirmDialog: () => void;

  isAddToCampaignConfirmOpen: boolean;
  pendingAddToCampaign: ProgramItem | null;
  proceedWithAddToCampaign: () => void;
  closeAddToCampaignConfirmDialog: () => void;

  // Loading states
  inviteLoading: boolean;
  addToCommunityLoading: boolean;
  addToProgramLoading: boolean;
}

export const CreatorOverviewActionMenu: React.FC<CreatorOverviewActionMenuProps> = ({
  creator,
  filteredCampaigns,
  filteredGroups,
  campaignSearchTerm,
  setCampaignSearchTerm,
  groupSearchTerm,
  setGroupSearchTerm,
  onInviteToCampaign,
  onAddToGroup,
  onAddToCampaign,
  igdmActive,
  disabled = false,

  // Confirmation dialog props
  isInviteToCampaignConfirmOpen,
  pendingInviteToCampaign,
  proceedWithInviteToCampaign,
  closeInviteToCampaignConfirmDialog,

  isAddToGroupConfirmOpen,
  pendingAddToGroup,
  proceedWithAddToGroup,
  closeAddToGroupConfirmDialog,

  isAddToCampaignConfirmOpen,
  pendingAddToCampaign,
  proceedWithAddToCampaign,
  closeAddToCampaignConfirmDialog,

  // Loading states
  inviteLoading,
  addToCommunityLoading,
  addToProgramLoading,
}) => {
  // Check if creator can be invited (has email or is in IG marketplace when IGDM is active)
  const canBeInvited = React.useMemo(() => {
    const hasEmail = creator.email && creator.email.trim() !== '';
    const inCreatorMarketplace = creator.inCreatorMarketplace === true;

    if (igdmActive) {
      return hasEmail || inCreatorMarketplace;
    } else {
      return hasEmail;
    }
  }, [creator.email, creator.inCreatorMarketplace, igdmActive]);

  const getInviteDisabledReason = () => {
    if (!canBeInvited) {
      return igdmActive
        ? 'Creator has no email address and is not in Instagram Creator Marketplace'
        : 'Creator has no email address';
    }
    return null;
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className="flex items-center gap-2"
            data-dd-action-name="instagram-creator-overview-actions"
          >
            Actions
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              data-dd-action-name="instagram-creator-overview-actions-invite"
              aria-label={canBeInvited ? 'Invite creator' : getInviteDisabledReason()}
              className="cursor-pointer"
              disabled={!canBeInvited}
            >
              Invite
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <div className="p-2">
                <Input
                  placeholder="Search campaigns..."
                  value={campaignSearchTerm}
                  onChange={(e) => setCampaignSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="mb-2"
                  data-dd-action-name="instagram-creator-overview-actions-search-campaigns"
                />
                {filteredCampaigns?.map((program) => (
                  <DropdownMenuItem
                    key={String(program.id)}
                    onSelect={() => {
                      onInviteToCampaign(program);
                      setCampaignSearchTerm('');
                    }}
                    className="cursor-pointer"
                    data-dd-action-name={`instagram-creator-overview-actions-select-campaign-${program.id}`}
                  >
                    {program.title}
                  </DropdownMenuItem>
                ))}
                {filteredCampaigns?.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground">
                    No campaigns found
                  </div>
                )}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              data-dd-action-name="instagram-creator-overview-actions-add-to-group"
              aria-label="Add creator to group"
              className="cursor-pointer"
            >
              Add to Group
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <div className="p-2">
                <Input
                  placeholder="Search groups..."
                  value={groupSearchTerm}
                  onChange={(e) => setGroupSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="mb-2"
                  data-dd-action-name="instagram-creator-overview-actions-search-groups"
                />
                {filteredGroups?.map((group) => (
                  <DropdownMenuItem
                    key={String(group.id)}
                    onSelect={() => {
                      onAddToGroup(group);
                      setGroupSearchTerm('');
                    }}
                    className="cursor-pointer"
                    data-dd-action-name={`instagram-creator-overview-actions-select-group-${group.id}`}
                  >
                    {group.title}
                  </DropdownMenuItem>
                ))}
                {filteredGroups?.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground">
                    No groups found
                  </div>
                )}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              data-dd-action-name="instagram-creator-overview-actions-add-to-campaign"
              aria-label="Skip invite and add to campaign"
              className="cursor-pointer"
            >
              Skip Invite & Add to Campaign
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <div className="p-2">
                <Input
                  placeholder="Search campaigns..."
                  value={campaignSearchTerm}
                  onChange={(e) => setCampaignSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="mb-2"
                  data-dd-action-name="instagram-creator-overview-actions-search-campaigns-add"
                />
                {filteredCampaigns?.map((program) => (
                  <DropdownMenuItem
                    key={`add-${String(program.id)}`}
                    onSelect={() => {
                      onAddToCampaign(program);
                      setCampaignSearchTerm('');
                    }}
                    className="cursor-pointer"
                    data-dd-action-name={`instagram-creator-overview-actions-add-to-campaign-${program.id}`}
                  >
                    {program.title}
                  </DropdownMenuItem>
                ))}
                {filteredCampaigns?.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground">
                    No campaigns found
                  </div>
                )}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation Dialogs */}
      <AlertDialog open={isInviteToCampaignConfirmOpen} onOpenChange={closeInviteToCampaignConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Campaign Invite</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingInviteToCampaign ? (
                `Are you sure you want to invite @${creator.username} to the campaign '${pendingInviteToCampaign.title}'?`
              ) : (
                'Please select a campaign.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={closeInviteToCampaignConfirmDialog}
              data-dd-action-name="instagram-creator-overview-actions-cancel-campaign-invite"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={proceedWithInviteToCampaign}
              disabled={!pendingInviteToCampaign || inviteLoading}
              data-dd-action-name="instagram-creator-overview-actions-confirm-campaign-invite"
            >
              {inviteLoading ? 'Sending Invite...' : 'Confirm Invite'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isAddToGroupConfirmOpen} onOpenChange={closeAddToGroupConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Add to Group</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAddToGroup ? (
                `Are you sure you want to add @${creator.username} to the group '${pendingAddToGroup.title}'?`
              ) : (
                'Please select a group.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={closeAddToGroupConfirmDialog}
              data-dd-action-name="instagram-creator-overview-actions-cancel-group-add"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={proceedWithAddToGroup}
              disabled={!pendingAddToGroup || addToCommunityLoading}
              data-dd-action-name="instagram-creator-overview-actions-confirm-group-add"
            >
              {addToCommunityLoading ? 'Adding...' : 'Add to Group'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isAddToCampaignConfirmOpen} onOpenChange={closeAddToCampaignConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Skip Invite & Add to Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAddToCampaign ? (
                `Are you sure you want to skip invite and add @${creator.username} to the campaign '${pendingAddToCampaign.title}'?`
              ) : (
                'Please select a campaign.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={closeAddToCampaignConfirmDialog}
              data-dd-action-name="instagram-creator-overview-actions-cancel-campaign-add"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={proceedWithAddToCampaign}
              disabled={!pendingAddToCampaign || addToProgramLoading}
              data-dd-action-name="instagram-creator-overview-actions-confirm-campaign-add"
            >
              {addToProgramLoading ? 'Adding...' : 'Add to Campaign'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
